import React from 'react';
import { GlassCard } from '../components/canvas/GlassCard';
import { SlideLogo } from '../components/canvas/SlideLogo';
import { SlideContent } from '../components/canvas/SlideContent';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';

/**
 * Forest Classic layout — the original Magic Forest layout.
 * Logo + header at top-left, glass card with content at bottom.
 *
 * Footer behavior:
 *   - Default (no nudge): footer renders inside the glass card (original design)
 *   - When content is nudged: footer moves out and pins to the slide bottom
 */
export function ForestClassicLayout({ data, fontPreset, logoEnabled = true, logoUrl = null, logoTitle = '', logoSubtitle = '', logoSize = null, logoIsDark = false, pc }) {
  const { layoutNudge } = usePreviewInteraction();

  // Check if content has been nudged away from default
  const contentNudge = layoutNudge?.content;
  const isNudged = contentNudge && (contentNudge.dy !== 0 || contentNudge.dx !== 0);

  return (
    <>
      {/* Header with logo */}
      {logoEnabled && (
        <NudgeableGroup groupId="logo" className="absolute left-[64px] right-[64px] top-[64px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <SlideLogo className="h-20 w-20" style={{ color: pc.text(0.90) }} logoUrl={logoUrl} size={logoSize} isDark={logoIsDark} />
              {logoTitle && (
                <div className="leading-tight translate-y-[5px]">
                  {logoTitle && (
                  <div
                    className="text-[28px] tracking-[0.22em] font-medium"
                    style={{ marginRight: '-0.22em', color: pc.text(0.75) }}
                  >
                    {logoTitle.toUpperCase()}
                  </div>
                  )}
                  {logoSubtitle && (
                    <div className="text-[21px] lowercase flex justify-between" style={{ color: pc.text(0.60) }}>
                      {logoSubtitle.split('').map((char, i) => (
                        <span key={i}>{char}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </NudgeableGroup>
      )}

      {/* Main content card */}
      <NudgeableGroup groupId="content" className="absolute bottom-[64px] left-[64px] right-[64px]">
        <GlassCard className="relative p-[56px]" blurLevel={data.glassBlur ?? 3} pc={pc}>
          <SlideContent data={data} fontPreset={fontPreset} pc={pc} options={{ showFooter: !isNudged }} />
        </GlassCard>
      </NudgeableGroup>

      {/* Footer — pinned at slide bottom, only when content is nudged away */}
      {isNudged && (
        <div className="absolute bottom-[64px] left-[64px] right-[64px] px-[56px] pb-[56px]">
          <SlideFooter data={data} pc={pc} />
        </div>
      )}
    </>
  );
}
